import React from 'react';
import api from '../../../../Api';
import { ShareTargetType } from './ShareTargetType';

export const Action = {
	ADD_SHARE_TARGET: 'ADD_SHARE_TARGET',
	REMOVE_SHARE_TARGET: 'REMOVE_SHARE_TARGET',
	CLEAR_SHARE_TARGETS: 'CLEAR_SHARE_TARGETS',
};

export default function useShareTargets(
	sku,
	options = { doIncludeGenreCommunities: true },
) {
	const [isLoading, setIsLoading] = React.useState(true);

	const [joinedCommunitiesData, setJoinedCommunitiesData] = React.useState({
		affiliations: [],
	});

	const [communityTargetIds, setCommunityTargetIds] = React.useState([]);

	const [genreCommunityOptions, setGenreCommunityOptions] = React.useState(
		[],
	);

	const [
		isLoadingGenreCommunityOptions,
		setIsLoadingGenreCommunityOptions,
	] = React.useState(true);

	const [doShareWithPublisher, setDoShareWithPublisher] = React.useState(
		true,
	);

	const [doShareWithFriends, setDoShareWithFriends] = React.useState(true);

	const [doShareWithColleagues, setDoShareWithColleagues] = React.useState(
		true,
	);

	const getAsArray = x => {
		return Array.isArray(x) ? x : [];
	};

	React.useEffect(() => {
		let request;

		const doesAssessmentExist = async () => {
			request = api.cancelableGet(`/api/me/reviews/${sku}/review`);
			const currentReview = await request.promise;
			return currentReview !== null;
		};

		const fetchUsersJoinedCommunities = async () => {
			const url = '/api/affiliations/me';
			request = api.cancelableGet(url);

			const communitiesData = await request.promise;

			if (communitiesData) {
				setJoinedCommunitiesData(communitiesData);
				return communitiesData;
			}
			return {};
		};

		const fetchReviewSharingState = async (
			usersCommunities = [],
			assessmentDoesExist,
		) => {
			const url = `/api/me/reviews/products/${sku}/communities/sharing`;
			request = api.cancelableGet(url);

			let {
				affiliationShareExceptions,
				ePlusGroupShareExceptions,
				externalSubmissionGroupInclusions,
			} = await request.promise;

			affiliationShareExceptions = getAsArray(affiliationShareExceptions);
			ePlusGroupShareExceptions = getAsArray(ePlusGroupShareExceptions);

			externalSubmissionGroupInclusions = getAsArray(
				externalSubmissionGroupInclusions,
			);

			const eplusGroupTypesToExcludeFromShare = ePlusGroupShareExceptions.map(
				groupExclusion => groupExclusion.communityType,
			);

			const communityIdsToExclude = affiliationShareExceptions.map(
				a => a.targetGroupId,
			);

			setCommunityTargetIds(() => {
				return usersCommunities
					.filter(c => !communityIdsToExclude.includes(c.id))
					.map(c => c.id);
			});

			if (
				eplusGroupTypesToExcludeFromShare.includes(
					ShareTargetType.friends,
				)
			) {
				setDoShareWithFriends(false);
			}

			if (
				eplusGroupTypesToExcludeFromShare.includes(
					ShareTargetType.colleagues,
				)
			) {
				setDoShareWithColleagues(false);
			}

			if (
				assessmentDoesExist &&
				!externalSubmissionGroupInclusions.includes(
					ShareTargetType.publisher,
				)
			) {
				setDoShareWithPublisher(false);
			}

			return communityIdsToExclude;
		};

		const fetchGenreCommunities = async (
			communityIdsToExcludeFromSharing,
			doesAssessmentExist,
		) => {
			const url = `/api/affiliations/genre/products/${sku}`;
			request = api.cancelableGet(url);

			try {
				let genreCommunities = await request.promise;
				genreCommunities = getAsArray(genreCommunities);

				const getActiveGenreCommunityIds = () => {
					const genreCommunityOptionIds = genreCommunities.map(
						c => c.id,
					);
					if (doesAssessmentExist) {
						return genreCommunityOptionIds.filter(
							id =>
								!communityIdsToExcludeFromSharing.includes(id),
						);
					}
					return genreCommunityOptionIds;
				};

				setCommunityTargetIds(ids => {
					return [...ids, ...getActiveGenreCommunityIds()];
				});

				setGenreCommunityOptions(genreCommunities);
				setIsLoadingGenreCommunityOptions(false);
			} catch (err) {
				console.error(err.message);
				setIsLoadingGenreCommunityOptions(false);
			}
		};

		const initialize = async () => {
			const assessmentExists = await doesAssessmentExist();

			const { affiliations } = await fetchUsersJoinedCommunities();

			const usersCommunities = getCommunities(affiliations);

			const communityIdsToExcludeFromSharing = await fetchReviewSharingState(
				usersCommunities,
				assessmentExists,
			);

			if (options.doIncludeGenreCommunities) {
				fetchGenreCommunities(
					communityIdsToExcludeFromSharing,
					assessmentExists,
				);
			}

			setIsLoading(false);
		};

		initialize();

		return () => {
			api.revoke(request);
		};
	}, [sku]);

	function getCommunities(communities = joinedCommunitiesData.affiliations) {
		const memberOfCommunities = communities.filter(
			c => typeof c.member === 'boolean' && c.member,
		);

		return memberOfCommunities.filter(a => !a.isGenreCommunity);
	}

	const activateAllShareTargetOptions = () => {
		setCommunityTargetIds(() => {
			return [
				...getCommunities().map(a => a.id),
				...genreCommunityOptions.map(c => c.id),
			];
		});

		if (!doShareWithFriends) {
			setDoShareWithFriends(true);
		}

		if (!doShareWithPublisher) {
			setDoShareWithPublisher(true);
		}

		if (!doShareWithColleagues) {
			setDoShareWithColleagues(true);
		}
	};

	const removeAllShareTargetOptions = () => {
		if (communityTargetIds.length > 0) {
			setCommunityTargetIds([]);
		}

		if (doShareWithFriends) {
			setDoShareWithFriends(false);
		}

		if (doShareWithPublisher) {
			setDoShareWithPublisher(false);
		}

		if (doShareWithColleagues) {
			setDoShareWithColleagues(false);
		}
	};

	const addShareTarget = action => {
		const { payload } = action;
		const { shareTarget } = payload;
		switch (shareTarget.type) {
			case ShareTargetType.generalCommunity:
			case ShareTargetType.genreCommunity:
				setCommunityTargetIds(ids => [...ids, shareTarget.id]);
				break;
			case ShareTargetType.all:
				activateAllShareTargetOptions();
				break;
			case ShareTargetType.friends:
				setDoShareWithFriends(true);
				break;
			case ShareTargetType.colleagues:
				setDoShareWithColleagues(true);
				break;
			case ShareTargetType.publisher:
				setDoShareWithPublisher(true);
				break;
			default:
				break;
		}
	};

	const removeShareTarget = action => {
		const { payload } = action;
		const { shareTarget } = payload;
		switch (shareTarget.type) {
			case ShareTargetType.generalCommunity:
			case ShareTargetType.genreCommunity:
				setCommunityTargetIds(ids =>
					ids.filter(id => id !== shareTarget.id),
				);
				break;
			case ShareTargetType.all:
				removeAllShareTargetOptions();
				break;
			case ShareTargetType.friends:
				setDoShareWithFriends(false);
				break;
			case ShareTargetType.colleagues:
				setDoShareWithColleagues(false);
				break;
			case ShareTargetType.publisher:
				setDoShareWithPublisher(false);
				break;
			default:
				break;
		}
	};

	const isShareTypeAllActive = () => {
		const joinedCommunitiesCt = getCommunities().length;
		const genreCommunitiesCt = genreCommunityOptions.length;

		const communityTargetsCt = communityTargetIds.length;

		const areAllDefaultShareTypesActive =
			joinedCommunitiesCt + genreCommunitiesCt === communityTargetsCt &&
			doShareWithFriends &&
			doShareWithColleagues &&
			doShareWithPublisher;

		return areAllDefaultShareTypesActive;
	};

	const reduceState = action => {
		switch (action.type) {
			case Action.ADD_SHARE_TARGET:
				addShareTarget(action);
				break;
			case Action.REMOVE_SHARE_TARGET:
				removeShareTarget(action);
				break;
			case Action.CLEAR_SHARE_TARGETS:
				removeAllShareTargetOptions();
				break;
			default:
				break;
		}
	};

	const isActiveShareTarget = shareTarget => {
		switch (shareTarget.type) {
			case ShareTargetType.generalCommunity:
			case ShareTargetType.genreCommunity:
				return communityTargetIds.includes(shareTarget.id);
			case ShareTargetType.all:
				return isShareTypeAllActive();
			case ShareTargetType.friends:
				return doShareWithFriends;
			case ShareTargetType.colleagues:
				return doShareWithColleagues;
			case ShareTargetType.publisher:
				return doShareWithPublisher;
			default:
				return false;
		}
	};

	const getCommunityIdsToNotShareWith = () => {
		const communityIdsToNotShareWith = [];

		const allAvailableCommunityIds = [
			...getCommunities().map(a => a.id),
			...genreCommunityOptions.map(c => c.id),
		];

		allAvailableCommunityIds.forEach(id => {
			if (!communityTargetIds.includes(id)) {
				communityIdsToNotShareWith.push(id);
			}
		});

		return communityIdsToNotShareWith;
	};

	// If the user selects to "share" the assessment with a genre community
	// that they also are not a part of, we need to add the user as a member of
	// that community
	const getChosenGenreCommunityIdsToJoin = () => {
		const { affiliations: communities } = joinedCommunitiesData;

		const genreCommunityOptionIds = genreCommunityOptions.map(c => c.id);

		const joinedCommunityIds = communities
			.filter(c => typeof c.member === 'boolean' && c.member)
			.map(c => c.id);

		return communityTargetIds.filter(id => {
			const userIsMember = joinedCommunityIds.includes(id);
			const isGenreCommunityOption = genreCommunityOptionIds.includes(id);
			return !userIsMember && isGenreCommunityOption;
		});
	};

	return {
		getCommunities,
		getGenreCommunityOptionsState: () => ({
			isLoading: isLoadingGenreCommunityOptions,
			options: genreCommunityOptions,
		}),
		isLoading: () => isLoading,
		update: action => reduceState(action),
		isActive: isActiveShareTarget,
		getCommunityIdsToNotShareWith,
		getChosenGenreCommunityIdsToJoin,
	};
}
